/**
 * Product Inventory Report Export
 * Shows variant column, Cases as main qty + "X total units" subtitle,
 * lot/batch breakdown in detailed view.
 */

import { supabase } from '../../../../../supabaseClient';

const getReportStyles = () => `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; padding: 20px; font-size: 10px; line-height: 1.4; }
  .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
  .company-name { font-size: 16px; font-weight: bold; color: #333; }
  .report-title { font-size: 14px; color: #666; margin-top: 2px; }
  .report-date { font-size: 8px; color: #999; margin-top: 2px; }
  .stats { display: flex; justify-content: space-around; margin: 15px 0; gap: 10px; }
  .stat-card { flex: 1; text-align: center; padding: 8px; border-radius: 4px; border: 1px solid #ddd; }
  .stat-card.total    { background: #e3f2fd; border-color: #1976d2; }
  .stat-card.in-stock { background: #e8f5e9; border-color: #388e3c; }
  .stat-card.low-stock{ background: #fff3e0; border-color: #f57c00; }
  .stat-card.out-stock{ background: #ffebee; border-color: #d32f2f; }
  .stat-number { font-size: 16px; font-weight: bold; margin-bottom: 2px; }
  .stat-number.total    { color: #1976d2; }
  .stat-number.in-stock { color: #388e3c; }
  .stat-number.low-stock{ color: #f57c00; }
  .stat-number.out-stock{ color: #d32f2f; }
  .stat-label { font-size: 8px; text-transform: uppercase; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; font-size: 9px; margin-top: 10px; }
  th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #ddd; vertical-align: top; }
  th { background-color: #f0f0f0; font-weight: bold; font-size: 8px; text-transform: uppercase; text-align: center; }
  tr:nth-child(even) { background-color: #f9f9f9; }
  .item-name { font-weight: bold; font-size: 9px; margin-bottom: 1px; }
  .item-details { font-size: 7px; color: #666; }
  .qty-main { font-weight: bold; font-size: 9px; }
  .qty-sub { font-size: 7.5px; color: #666; display: block; margin-top: 1px; }
  .center-align { text-align: center; }
  .status { padding: 1px 3px; border-radius: 2px; font-size: 7px; font-weight: bold; text-transform: uppercase;
            text-align: center; display: inline-block; min-width: 50px; }
  .status.in-stock    { background: #d4edda; color: #155724; }
  .status.low-stock   { background: #fff3cd; color: #856404; }
  .status.out-of-stock{ background: #f8d7da; color: #721c24; }
  .lot-row { background-color: #f5f5f5 !important; font-size: 8px; }
  .lot-indent { padding-left: 18px; font-size: 8px; color: #555; }
  .expiry-warn { color: #c62828; font-weight: bold; }
  .expiry-soon { color: #e65100; }
  @page { margin: 0; size: letter landscape; }
  @media print {
    body { margin: 0; padding: 10px; }
    tr { page-break-inside: avoid; }
    table { page-break-inside: auto; }
  }
`;

// ── sorting (mirrors ProductTable.jsx) ───────────────────────────────────────

const extractSize = (name) => {
  const m = name?.match(/(\d+(\.\d+)?)/);
  return m ? parseFloat(m[1]) : 0;
};

const sortInventory = (inventory) =>
  [...inventory].sort((a, b) => {
    const nameA = (a.item_name || '').toLowerCase();
    const nameB = (b.item_name || '').toLowerCase();
    if (nameA !== nameB) return nameA.localeCompare(nameB);
    const sizeA = extractSize(a.variant_name || a.item_name);
    const sizeB = extractSize(b.variant_name || b.item_name);
    return sizeB - sizeA; // largest size first
  });

// ── helpers ──────────────────────────────────────────────────────────────────

const getStockStatus = (item) => {
  const qty = item.total_base_units || item.total_quantity || 0;
  const rp  = item.reorder_point || 0;
  if (qty === 0)            return { label: 'Out of Stock', cls: 'out-of-stock' };
  if (rp > 0 && qty <= rp) return { label: 'Low Stock',    cls: 'low-stock' };
  return                          { label: 'In Stock',     cls: 'in-stock' };
};

/**
 * Compute aggregate-level cases from all lots (each lot may have its own units_per_case).
 * Falls back to a simple division using the aggregate's unitsPerCase.
 */
const computeCases = (item) => {
  const lots = item.lots || [];
  const totalBase = item.total_base_units || item.total_quantity || 0;
  const unitsPerCase = item.units_per_case || 1;

  if (lots.length > 0) {
    return lots.reduce((sum, lot) => {
      const upc = lot.units_per_case || unitsPerCase;
      return sum + Math.floor((lot.total_base_units || 0) / upc);
    }, 0);
  }
  return Math.floor(totalBase / unitsPerCase);
};

const formatCasesHtml = (cases, totalUnits) => {
  const casesStr = cases > 0 ? `${cases.toLocaleString()} cases` : '0 cases';
  return `<span class="qty-main">${casesStr}</span><span class="qty-sub">${totalUnits.toLocaleString()} total units</span>`;
};

const formatLotCasesHtml = (lot, fallbackUnitsPerCase) => {
  const totalBase = lot.total_base_units || 0;
  const upc = lot.units_per_case || fallbackUnitsPerCase || 1;
  const cases = Math.floor(totalBase / upc);
  const casesStr = cases > 0 ? `${cases.toLocaleString()} cases` : '0 cases';
  return `<span class="qty-main">${casesStr}</span><span class="qty-sub">${totalBase.toLocaleString()} total units</span>`;
};

const formatExpiry = (dateStr) => {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const in30 = new Date(today); in30.setDate(today.getDate() + 30);
  const label = date.toLocaleDateString();
  if (date < today) return `<span class="expiry-warn">${label} (Expired)</span>`;
  if (date < in30)  return `<span class="expiry-soon">${label} (Soon)</span>`;
  return label;
};

const calcStats = (inventory) => {
  const totalItems = inventory.length;
  const inStock    = inventory.filter(i => (i.total_base_units || i.total_quantity || 0) > 0).length;
  const lowStock   = inventory.filter(i => {
    const qty = i.total_base_units || i.total_quantity || 0;
    const rp  = i.reorder_point || 0;
    return rp > 0 && qty <= rp && qty > 0;
  }).length;
  const outOfStock = inventory.filter(i => (i.total_base_units || i.total_quantity || 0) <= 0).length;
  return { totalItems, inStock, lowStock, outOfStock };
};

const statsBlock = (stats) => `
  <div class="stats">
    <div class="stat-card total">
      <div class="stat-number total">${stats.totalItems}</div>
      <div class="stat-label">Total Products</div>
    </div>
    <div class="stat-card in-stock">
      <div class="stat-number in-stock">${stats.inStock}</div>
      <div class="stat-label">In Stock</div>
    </div>
    <div class="stat-card low-stock">
      <div class="stat-number low-stock">${stats.lowStock}</div>
      <div class="stat-label">Low Stock</div>
    </div>
    <div class="stat-card out-stock">
      <div class="stat-number out-stock">${stats.outOfStock}</div>
      <div class="stat-label">Out of Stock</div>
    </div>
  </div>
`;

// ── Summary Report ────────────────────────────────────────────────────────────

const generateProductSummary = (companyName, inventory) => {
  const sorted = sortInventory(inventory);
  const stats = calcStats(sorted);

  const rows = sorted.map(item => {
    const status     = getStockStatus(item);
    const totalBase  = item.total_base_units || item.total_quantity || 0;
    const cases      = computeCases(item);
    const casesHtml  = formatCasesHtml(cases, totalBase);
    const rp         = item.reorder_point > 0 ? `${item.reorder_point.toLocaleString()} units` : '-';
    const variant    = item.variant_name || '-';

    return `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${variant}</td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align">${casesHtml}</td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${status.cls}">${status.label}</span></td>
      </tr>
    `;
  }).join('');

  return `<!DOCTYPE html><html>
    <head><title>Product Inventory Report — Summary</title><style>${getReportStyles()}</style></head>
    <body>
      <div class="header">
        <div class="company-name">${companyName}</div>
        <div class="report-title">Product Inventory Report — Summary</div>
        <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
      </div>
      ${statsBlock(stats)}
      <table>
        <thead>
          <tr>
            <th width="22%">Product</th>
            <th width="14%">Variant</th>
            <th width="14%">Location</th>
            <th width="20%">Cases</th>
            <th width="15%">Reorder Point</th>
            <th width="15%">Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </body>
  </html>`;
};

// ── Detailed Report (lot/batch breakdown) ────────────────────────────────────

const generateProductDetailed = (companyName, inventory) => {
  const sorted = sortInventory(inventory);
  const stats = calcStats(sorted);

  const rows = sorted.map(item => {
    const status    = getStockStatus(item);
    const totalBase = item.total_base_units || item.total_quantity || 0;
    const cases     = computeCases(item);
    const casesHtml = formatCasesHtml(cases, totalBase);
    const rp        = item.reorder_point > 0 ? `${item.reorder_point.toLocaleString()} units` : '-';
    const variant   = item.variant_name || '-';
    const unitsPerCase = item.units_per_case || 1;

    const activeLots = (item.lots || []).filter(l => l.is_active && (l.total_base_units || 0) > 0);

    const headerRow = `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${variant}</td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align"><strong>${activeLots.length}</strong></td>
        <td class="center-align">${casesHtml}</td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${status.cls}">${status.label}</span></td>
      </tr>
    `;

    const lotRows = activeLots.map(lot => {
      const lotCasesHtml = formatLotCasesHtml(lot, unitsPerCase);
      const mfgDate = lot.manufactured_date
        ? new Date(...lot.manufactured_date.split('-').map(Number)).toLocaleDateString()
        : null;
      const expiryHtml = formatExpiry(lot.expiry_date);
      const lotLabel = [
        lot.lot_number   ? `Lot: ${lot.lot_number}`   : null,
        lot.batch_number ? `Batch: ${lot.batch_number}` : null
      ].filter(Boolean).join('  ');

      return `
        <tr class="lot-row">
          <td class="lot-indent">${lotLabel || '(no lot/batch)'}</td>
          <td></td>
          <td></td>
          <td></td>
          <td class="center-align">${lotCasesHtml}</td>
          <td class="center-align">
            ${mfgDate ? `Mfg: ${mfgDate}<br/>` : ''}
            Exp: ${expiryHtml}
            ${lot.shelf_life_months ? `<br/><span style="font-size:7px;color:#888;">${lot.shelf_life_months} mo shelf life</span>` : ''}
          </td>
          <td></td>
        </tr>
      `;
    }).join('');

    return headerRow + lotRows;
  }).join('');

  return `<!DOCTYPE html><html>
    <head><title>Product Inventory Report — Detailed</title><style>${getReportStyles()}</style></head>
    <body>
      <div class="header">
        <div class="company-name">${companyName}</div>
        <div class="report-title">Product Inventory Report — Detailed (Lot Breakdown)</div>
        <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
      </div>
      ${statsBlock(stats)}
      <table>
        <thead>
          <tr>
            <th width="20%">Product</th>
            <th width="12%">Variant</th>
            <th width="10%">Location</th>
            <th width="6%">Lots</th>
            <th width="20%">Cases</th>
            <th width="18%">Dates</th>
            <th width="14%">Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </body>
  </html>`;
};

// ── Public export ─────────────────────────────────────────────────────────────

export const exportProductInventoryReport = async (companyId, inventory, reportType = 'summary') => {
  let companyName = 'Company';
  try {
    const { data } = await supabase.from('companies').select('name').eq('id', companyId).single();
    if (data) companyName = data.name;
  } catch (_) {}

  const html = reportType === 'detailed'
    ? generateProductDetailed(companyName, inventory)
    : generateProductSummary(companyName, inventory);

  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:absolute;width:0;height:0;border:none;';
  iframe.src = url;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 1000);
  };
};
